import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Subject, forkJoin, merge } from 'rxjs';
import { debounceTime, finalize, switchMap, take, takeUntil } from 'rxjs/operators';
import { OperationalIndicatorService } from 'src/app/pages/dashboard/services/operational-indicator.service';
import { TopFilterService } from 'src/app/pages/dashboard/services/topFIlter.service';
import {
  ChartFilterControlsInterface,
  ReportInputTypes,
} from 'src/app/shared/models/common';
import { CancelHttpRequestService } from 'src/app/shared/services/cancel-request.service';
import { UtilityService } from 'src/app/utils/utility.service';

@Component({
  selector: 'app-clients',
  templateUrl: './clients.component.html',
  styleUrls: ['./clients.component.css'],
})
export class ClientsComponent implements OnInit {
  public topFiltersFormInstance: FormGroup;
  public hideFilters: boolean = false;
  public isLoading: boolean = false;
  public showSortControl: boolean = false;

  public productId: any;
  public tenantId = JSON.parse(localStorage.getItem('pf') || null).tenantId;

  public legends: { label: string; total: number }[] = [];
  public numberOfTenantBranches: number;

  public clientPalette: string[] = ['#929FB1', '#D4D9E2', '#4d5d76'];

  public clientsForm: FormGroup;
  public clientsFormControls: ChartFilterControlsInterface[] = [
    { name: ReportInputTypes.SORT, type: 'SORT', position: 'left' },
    {
      name: ReportInputTypes.LOAN_PRODUCT,
      type: 'LOAN_PRODUCT',
      position: 'right',
    },
  ];
  public clientsChartData?:
    | { name: string; active: number; pending: number; approved: number }[]
    | { name: string; value: number }[] = [];
  public clientsSortingOptions: {
    key: 'name' | 'value';
    sortingDirection: 'asc' | 'desc' | 'default';
  } = {
    key: 'name',
    sortingDirection: 'asc',
  };

  public form: FormGroup;

  public subject = new Subject<number>();
  public reportsInput = ReportInputTypes;

  public sortLabel = $localize`:Sort|label for sort@@sort:Sort`;

  public sortOptions = [
    {
      label: $localize`:Default|label for default@@default:Default`,
      value: 'default',
    },
    // { label: 'Value: Asc.', value: 'value-asc'},
    // { label: 'Value: Desc.', value: 'value-desc'},
    {
      label: $localize`:Name accending|label for name acending@@name_acending:Name acending`,
      value: 'name-asc',
    },
    {
      label: $localize`:Name decending|label for name decending@@name_decending:Name decending`,
      value: 'name-desc',
    },
  ];

  constructor(
    private operationalIndicatorService: OperationalIndicatorService,
    private topFilterService: TopFilterService,
    private utils: UtilityService,
    private fb: FormBuilder
  ) {
    this.topFiltersFormInstance = this.topFilterService.topFilterForm;
  }

  ngOnInit(): void {
    this.initForm();
    this.getClientsChartData();
    this.subject.next(Math.random());
  }

  private initForm() {
    this.form = this.fb.group({
      [ReportInputTypes.SORT]: new FormControl(`default`, [
        Validators.required,
      ]),
      [ReportInputTypes.LOAN_PRODUCT]: new FormControl(0, [
        Validators.required,
      ]),
    });
  }
  public get productCtrl() {
    return this.form.get(ReportInputTypes.LOAN_PRODUCT) as FormControl;
  }

  onSort(sortOption: any) {
    this.sortLabel = sortOption.label;
    this.filterClients({
      value: sortOption.value,
      name: ReportInputTypes.SORT,
      type: ReportInputTypes.SORT,
    });
  }

  getClientsChartData() {
    merge(
      this.topFilterService.topFilterForm.valueChanges,
      this.subject.asObservable()
    )
      .pipe(
        debounceTime(300),
        switchMap(() => {
          const { areaId, branchId, loanOfficerId, startDate, endDate } =
            this.topFiltersFormInstance.value;
          const { productId, tenantId } = this;

          const queryParams = new URLSearchParams({
            startDate: this.utils.formatDate_custom_yearFirst(startDate),
            endDate: this.utils.formatDate_custom_yearFirst(endDate),
            ...(areaId && { areaId }),
            ...(branchId && { branchId }),
            ...(loanOfficerId && { loanOfficerId }),
            ...(tenantId && { tenantId }),
            ...(productId && { productId }),
          });

          this.isLoading = true;
          return forkJoin([
            this.operationalIndicatorService.getClients(queryParams),
            this.operationalIndicatorService.getClientsSummaryTotal(
              queryParams
            ),
          ]);
        })
      )
      .subscribe({
        next: ([clients, summary]) => {
          this.isLoading = false;

          const { numberOfBranchesForTenant } = clients?.data?.total?.[0] || {};
          this.numberOfTenantBranches = numberOfBranchesForTenant;

          if ((clients?.data?.data?.length ?? 0) < 2) {
            this.hideFilters = true;

            const row = clients?.data?.data?.[0] || {};
            const getClientStatus = (status: string) => {
              switch (status.toLowerCase()) {
                case 'active':
                  return {
                    name: $localize`:Active|label for active@@active:Active`,
                    value: row.activeClients || 0,
                  };
                case 'pending':
                  return {
                    name: $localize`:Pending|label for pending@@pending:Pending`,
                    value: row.pendingClients || 0,
                  };
                case 'approved':
                  return {
                    name: $localize`:Approved|label for approved@@approved:Approved`,
                    value: row.approvedClients || 0,
                  };
                default:
                  return { name: status, value: 0 };
              }
            };

            this.clientsChartData = [
              getClientStatus('active'),
              getClientStatus('pending'),
              getClientStatus('approved'),
            ];

            this.showSortControl = false;
          } else {
            this.showSortControl = true;
            this.clientsChartData = clients.data.data.map((item: any) => ({
              name: item.branchName,
              bar3: item.pendingClients,
              bar2: item.activeClients,
              bar1: item.approvedClients,
            }));
          }

          // Summary
          const totalApprovedClients = summary?.data?.totalApprovedClients ?? 0;
          const totalActiveClients = summary?.data?.totalActiveClients ?? 0;
          const totalPendingClients = summary?.data?.totalPendingClients ?? 0;

          this.legends = [
            {
              label: $localize`:Approved|label for approved@@approved:Approved`,
              total: totalApprovedClients,
            },
            {
              label: $localize`:Active|label for active@@active:Active`,
              total: totalActiveClients,
            },
            {
              label: $localize`:Pending|label for pending@@pending:Pending`,
              total: totalPendingClients,
            },
          ];
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  public get hasClientData(): boolean {
    if (this.legends.some((l) => (l.total || 0) > 0)) return true;
    return Array.isArray(this.clientsChartData) &&
      this.clientsChartData.some((d: any) => (d.value || 0) > 0);
  }

  filterClients(ev: any) {
    switch (ev.type) {
      case ReportInputTypes.SORT:
        const selectedString =
          ev.value === 'default' ? 'name-default' : ev.value;
        const [key, sortingDirection] = selectedString.split('-');
        this.clientsSortingOptions = { key, sortingDirection };
        break;
      case ReportInputTypes.LOAN_PRODUCT:
        this.productId = ev.value;
        this.subject.next(Math.random());
        break;
    }
  }
}
